import { normalizeForSearch } from './search-normalize';

export const DISCOVERY_SEARCH_MAX_QUERY_LENGTH = 256;
export const DISCOVERY_SEARCH_MAX_QUERY_TOKENS = 12;
export const DISCOVERY_SEARCH_MAX_TOKEN_LENGTH = 96;
export const DISCOVERY_SEARCH_MAX_FIELD_LENGTH = 32_768;
export const DISCOVERY_SEARCH_MAX_FIELD_TOKENS = 4_096;

const DISCOVERY_TOKEN_RE = /[\p{L}\p{N}]+|[\p{S}]+/gu;

export interface DiscoveryQuery {
  normalized: string;
  tokens: string[];
  valid: boolean;
}

export interface DiscoveryField {
  normalized: string;
  tokens: readonly string[];
  weight: number;
}

export function boundDiscoverySearchInput(value: string): string {
  return value.slice(0, DISCOVERY_SEARCH_MAX_QUERY_LENGTH);
}

export function tokenizeDiscoveryText(normalized: string): string[] {
  return normalized.match(DISCOVERY_TOKEN_RE) ?? [];
}

export function discoveryField(value: string, weight = 0): DiscoveryField {
  // Durable contracts intentionally allow long authored fields. Search keeps
  // only a bounded local derivative so one pathological name, note, or
  // testimony cannot dominate main-thread normalization or retained memory.
  let bounded = value.slice(0, DISCOVERY_SEARCH_MAX_FIELD_LENGTH);
  const tail = bounded.charCodeAt(bounded.length - 1);
  if (tail >= 0xd800 && tail <= 0xdbff) bounded = bounded.slice(0, -1);
  const normalized = normalizeForSearch(
    bounded,
  );
  const tokens = [...new Set(tokenizeDiscoveryText(normalized))]
    .filter((token) => token.length <= DISCOVERY_SEARCH_MAX_TOKEN_LENGTH)
    .slice(0, DISCOVERY_SEARCH_MAX_FIELD_TOKENS)
    .sort();
  return {
    normalized,
    tokens,
    weight,
  };
}

export function prepareDiscoveryQuery(value: string): DiscoveryQuery {
  const normalized = normalizeForSearch(boundDiscoverySearchInput(value));
  const tokens = [...new Set(tokenizeDiscoveryText(normalized))];
  return {
    normalized,
    tokens,
    valid:
      tokens.length <= DISCOVERY_SEARCH_MAX_QUERY_TOKENS &&
      tokens.every((token) => token.length <= DISCOVERY_SEARCH_MAX_TOKEN_LENGTH),
  };
}

export function lowerBoundStrings(values: readonly string[], target: string): number {
  let low = 0;
  let high = values.length;
  while (low < high) {
    const middle = (low + high) >> 1;
    if (values[middle] < target) low = middle + 1;
    else high = middle;
  }
  return low;
}

/**
 * 3 = exact token, 2 = token prefix, 1 = mid-token substring, 0 = miss.
 * One- through three-character words deliberately stop at prefix matching so
 * a short name like "Son" cannot light up "Johnson" mid-word.
 */
export function discoveryWordQuality(
  word: string,
  field: Pick<DiscoveryField, 'normalized' | 'tokens'>,
): 0 | 1 | 2 | 3 {
  const exactAt = lowerBoundStrings(field.tokens, word);
  if (field.tokens[exactAt] === word) return 3;
  if (field.tokens[exactAt]?.startsWith(word)) return 2;
  return word.length >= 4 && field.normalized.includes(word) ? 1 : 0;
}

/** Strict AND across query tokens; exact always outranks prefix, then substring. */
export function scoreDiscoveryFields(
  query: DiscoveryQuery,
  fields: readonly DiscoveryField[],
): number | null {
  if (!query.valid || query.tokens.length === 0) return null;
  let score = 0;
  for (const word of query.tokens) {
    let best = 0;
    for (const field of fields) {
      const quality = discoveryWordQuality(word, field);
      if (quality > 0) best = Math.max(best, quality * 100 + field.weight);
    }
    if (best === 0) return null;
    score += best;
  }
  return score;
}

export function compareDiscoveryStrings(left: string, right: string): number {
  return left < right ? -1 : left > right ? 1 : 0;
}
